import { useTranslate } from '@refinedev/core';
import { Avatar, Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import { CloudOutlined } from '@ant-design/icons';
import type { OAuthAccountOut, UserOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { OAuthProviderTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';
import { OAUTH_ACCOUNT_SHOW_TABS } from '../constants';
import type { OAuthAccountShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<OAuthAccountShowTab, string> = {
  general: 'iam.oauth_accounts.tabs.general',
};

interface OAuthAccountShowHeaderProps {
  record: OAuthAccountOut;
  user?: UserOut;
  activeTab: OAuthAccountShowTab;
  onTabChange: (key: string) => void;
}

export const OAuthAccountShowHeader = ({
  record,
  user,
  activeTab,
  onTabChange,
}: OAuthAccountShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <Avatar
          size={64}
          icon={<CloudOutlined />}
          style={{ backgroundColor: token.colorPrimary }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long emails wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.account_email || `#${record.id}`}
            </Title>
            <OAuthProviderTag provider={record.oauth_name} />
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('iam.oauth_accounts.fields.user')}>
              <ShowIconLink resource="iam.users" id={record.user_id}>
                {user?.email ?? user?.username ?? record.user_id}
              </ShowIconLink>
            </StatCell>
            <StatCell label={t('iam.oauth_accounts.fields.account_id')}>
              {orDash(record.account_id)}
            </StatCell>
            <StatCell label={t('base.fields.created_at')}>
              {orDash(formatDateTime(record.created_at))}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={OAUTH_ACCOUNT_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
